import { useEffect, useState } from 'react';

import { expVal } from '@confluence/feature-experiments';
import type { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import { useFeatureExposedAnalytics } from './useFeatureExposedAnalytics.ts';
import { useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment } from './useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

export const useUpflowV2ConfluenceStandardFGsExperiment = ({
	skip,
	touchpointId,
	currentEdition,
	targetEdition,
}: {
	skip: boolean;
	touchpointId: string;
	currentEdition: ConfluenceEdition | null;
	targetEdition: ConfluenceEdition | null;
}): {
	cohort: string | null;
	isSiteAdmin: boolean;
	isBillingAdmin: boolean | null | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
} => {
	const fireExperimentExposureEvent = useFeatureExposedAnalytics();
	const [abCohort, setAbCohort] = useState<string | null>(null);

	const {
		isEligible,
		isSiteAdmin,
		isBillingAdmin,
		error: eligibilityHookError,
		loading: eligibilityHookLoading,
	} = useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment({
		skip,
		currentEdition,
		targetEdition,
	});

	useEffect(() => {
		if (isEligible) {
			fireExperimentExposureEvent({
				flagKey: 'grw-1930_confluence_upv2_standard_ab',
				cohort: expVal('grw-1930_confluence_upv2_standard_ab', 'cohort', 'not-enrolled'),
				growthInitiative: 'luna',
				touchpointId,
				additionalAttributes: {
					currentEdition,
					targetEdition,
					isSiteAdmin,
					isBillingAdmin,
				},
			});

			setAbCohort(expVal('grw-1930_confluence_upv2_standard_ab', 'cohort', 'not-enrolled'));
		} else if (eligibilityHookError) {
			// Error occurred, reset to null
			setAbCohort(null);
		} else {
			setAbCohort('not-enrolled');
		}
	}, [
		fireExperimentExposureEvent,
		isEligible,
		eligibilityHookError,
		currentEdition,
		targetEdition,
		isSiteAdmin,
		isBillingAdmin,
		touchpointId,
	]);

	return {
		cohort: abCohort,
		isSiteAdmin,
		isBillingAdmin,
		loading: eligibilityHookLoading,
		error: eligibilityHookError,
	};
};
